import moment from 'moment-timezone';
import i18next from 'i18next';
import { format, parseISO } from 'date-fns';
import { enUS, th } from 'date-fns/locale';
import { getTimeZoneName } from 'utils/Timezones';
import { isFutureDate } from 'common/helpers/dateUtils';

const DATE_FORMAT = 'YYYY-MM-DD';
const TIME_FORMAT = 'HH:mm';

const LOCALE_MAPPING = {
  'en-US': enUS,
  'th-TH': th,
};

export const dateFromObjectId = function (objectId) {
  return new Date(parseInt(objectId.substring(0, 8), 16) * 1000);
};

// custom locale for apac english
moment.defineLocale('en-th', {
  parentLocale: 'en',
  longDateFormat: {
    LT: 'HH:mm',
    LTS: 'HH:mm:ss',
    L: 'DD/MM/YYYY',
    LL: 'D MMMM YYYY',
    LLL: 'D MMMM YYYY HH:mm',
    LLLL: 'dddd D MMMM YYYY HH:mm',
  },
});

export function getMomentDate(date) {
  if (!date) {
    return null;
  }
  return moment(date);
}

// export function extractLocaleSettingsContext({ localeData }) {
//   const dateFormat = localeData?.dateFormat || DEFAULT_DATE_FORMAT;
//   const timeFormat = localeData?.timeFormat || DEFAULT_TIME_FORMAT;
//   const localeCode = localeData?.localeCode || LOCALE_CODE;
//   const languageCodeId = localeData?.languageCodeId;
//   const dimensional_units =
//     localeData?.dimensional_units || DEFAULT_DISTANCE_UNITS;
//   const distance_units = localeData?.distance_units || DEFAULT_DISTANCE_UNITS;

//   return {
//     dateFormat,
//     timeFormat,
//     localeCode,
//     languageCodeId,
//     dimensional_units,
//     distance_units,
//   };
// }

export function createFormatDateWithContext({ dateFormat }) {
  return function (date, timezone) {
    return formatDate(date, { dateFormat, timezone });
  };
}

export function createFormatDateTimeWithContext({ dateFormat, timeFormat }) {
  return function (date, timezone, showTimezone = false) {
    return formatDate(date, {
      dateFormat,
      timeFormat,
      timezone,
      showTimezone,
    });
  };
}

export function formatDate(date, options = {}) {
  const { t } = options;
  if (!date || !moment(date).isValid()) {
    if (options.emptyText) {
      return options.emptyText;
    }
    if (t) {
      return t('NOT_SET');
    }
    return '-';
  }

  const { dateFormat, timeFormat, timezone } = options;

  const showTimezone = !!options.showTimezone;

  const items = [];
  if (dateFormat) {
    items.push(dateFormat);
  }

  if (timeFormat) {
    items.push(timeFormat);
  }

  const format = items.join(' ');

  if (moment.isMoment(date)) {
    return date.format(format);
  }

  if (showTimezone && timezone) {
    return `${moment(date).tz(timezone).format(format)} (${getTimeZoneName(
      timezone,
    )})`;
  }

  if (!showTimezone && timezone) {
    return moment(date).tz(timezone).format(format);
  }

  return moment(date).format(format);
}

// Uses native date JS object
export function formatDateV2(date) {
  if (!date) {
    return '-';
  }

  const locale = LOCALE_MAPPING[LOCALE_CODE] ?? enUS;

  return format(parseISO(date), DEFAULT_DATE_FORMAT_V2, { locale });
}

export function formatDateRange(windowStart, windowEnd, timezone) {
  if (!windowStart && !windowEnd) {
    return i18next.t('NOT_SET');
  }

  const start = moment.tz(windowStart, timezone);
  const end = moment.tz(windowEnd, timezone);
  const today = moment();

  if (start.isSame(end, 'day')) {
    if (today.isSame(start, 'year')) {
      return `${start.format('LL LT')} - ${end.format('LT')}`;
    }
    return `${start.format('L LT')} - ${end.format('LT')}`;
  }
  if (today.isSame(start, 'year')) {
    return `${start.format('L LT')} - ${end.format('L LT')}`;
  }
  return `${start.format('L LT')} - ${end.format('L LT')}`;
}

export function formatDateTime(date, timezone) {
  if (!date) {
    return null;
  }
  const formatString = 'MMM DD, LT';
  if (!timezone) {
    return moment(date).format(formatString);
  }

  return moment(date).tz(timezone).format(formatString);
}

export function fromDate(date) {
  return moment().from(date);
}

export function getElapsedTime(lastUpdated, options = {}) {
  if (!lastUpdated) {
    return null;
  }

  const showRelativeTimeIfPast24Hours = options.showRelativeTimeIfPast24Hours;
  const showTimeIfPast24Hours = options.showTimeIfPast24Hours;

  const time = moment(lastUpdated, moment.ISO_8601, true).isValid()
    ? new Date(lastUpdated)
    : new Date(lastUpdated.toDate()).toISOString();
  const momentDate = moment(time);
  const duration = moment.duration(moment().diff(momentDate));
  const durationLookup = {
    days: duration.days(),
    hours: duration.hours(),
    minutes: duration.minutes(),
    seconds: duration.seconds(),
  };

  const past24Hours = durationLookup.days >= 1;

  if (past24Hours && !showRelativeTimeIfPast24Hours) {
    if (showTimeIfPast24Hours) {
      return momentDate.format('LT - DD MMM YYYY');
    }
    return momentDate.format('DD MMM YYYY');
  }
  return momentDate.fromNow();
}

const convertTime12to24 = (time12h = '') => {
  const [time, modifier] = time12h.split(' ');

  let [hours, minutes] = time.split(':');

  if (hours === '12') {
    hours = '00';
  }

  if (modifier === 'PM') {
    hours = parseInt(hours, 10) + 12;
  }

  return `${hours}:${minutes}`;
};

// grab the date and time values from the datepicker and timepicker respectively
export function processDateTimeChange({ date, time, timezone, is24HourTime }) {
  const {
    formattedDate,
    formattedDateTime,
    validTimeEntered,
    validDateEntered,
    isDateInvalid,
  } = parseDateTime({ date, time, timezone, is24HourTime });

  let enteredDate;
  if (validTimeEntered) {
    enteredDate = moment.tz(formattedDateTime, timezone);
  } else if (validDateEntered) {
    enteredDate = moment(formattedDate);
  }
  const usingFutureDate = enteredDate
    ? isFutureDate(enteredDate, timezone)
    : false;
  return {
    date: enteredDate,
    isDateInvalid,
    validTimeEntered,
    validDateEntered,
    usingFutureDate,
  };
}

// moment-timezone accepts a date string formatted as YYYY-MM-DD HH:mm
export function parseDateTime({ date, time, timezone, is24HourTime }) {
  let momentDate = moment.isMoment(date) ? date.clone() : moment(date);
  if (!momentDate.isValid()) {
    return {
      validTimeEntered: false,
      validDateEntered: false,
      isDateInvalid: true,
      formattedDate: null,
      formattedDateTime: null,
      date: null,
    };
  }
  let convertedTime = time;
  const formattedDate = momentDate.format(DATE_FORMAT);
  if (time && (time.includes('AM') || time.includes('PM')) && !is24HourTime) {
    convertedTime = convertTime12to24(time);
  }
  const dateTimeString = `${formattedDate} ${convertedTime}`;

  const dateTimeMomentObj = moment.tz(dateTimeString, timezone);
  if (!dateTimeMomentObj.isValid()) {
    return {
      validDateEntered: true,
      validTimeEntered: false,
      isDateInvalid: true,
      formattedDate,
      formattedDateTime: null,
      date: null,
    };
  }
  return {
    formattedDate,
    formattedDateTime: dateTimeMomentObj.format(
      `${DATE_FORMAT} ${TIME_FORMAT}`,
    ),
    isDateInvalid: false,
    validTimeEntered: true,
    validDateEntered: true,
  };
}

export default {
  createFormatDateWithContext: createFormatDateWithContext,
  formatDate,
  fromDate,
  getMomentDate,
};
